import type {
  StudyArm,
  AucResult,
  PkParameters,
} from '@bioavail/shared-types';

const LN2 = Math.log(2);

/**
 * Compute secondary PK parameters per arm (PSD §4.2.7).
 * Cmax/Tmax (earliest on tie), t½ = ln(2)/λ_z, CL/F = Dose/AUC(0-∞),
 * Vz/F = CL/F / λ_z. Blocks on missing λ_z for t½ and Vz/F.
 *
 * Direct-AUC arms omit Cmax/Tmax/t½/Vz/F and report CL/F from Dose/directAuc.
 */
export function computePkParameters(
  arm: StudyArm,
  auc: AucResult,
  normalizedDose: number,
): PkParameters {
  // Direct-AUC edge case.
  if (arm.usesDirectAuc) {
    const directAuc = arm.directAuc ?? 0;
    const clOverF = directAuc > 0 ? normalizedDose / directAuc : null;
    return {
      armId: arm.id,
      cmax: 0,
      tmax: 0,
      halfLife: null,
      clOverF,
      vzOverF: null,
      directAuc: true,
    };
  }

  const points = [...arm.dataPoints].sort((a, b) => a.time - b.time);

  let cmax = points.length > 0 ? points[0]!.concentration : 0;
  let tmax = points.length > 0 ? points[0]!.time : 0;
  for (const p of points) {
    if (p.concentration > cmax) {
      cmax = p.concentration;
      tmax = p.time;
    }
  }

  const hasLambdaZ = auc.lambdaZ > 0;
  const halfLife = hasLambdaZ ? LN2 / auc.lambdaZ : null;

  const clOverF = auc.aucZeroToInf > 0 ? normalizedDose / auc.aucZeroToInf : null;
  const vzOverF = hasLambdaZ && clOverF !== null ? clOverF / auc.lambdaZ : null;

  return {
    armId: arm.id,
    cmax,
    tmax,
    halfLife,
    clOverF,
    vzOverF,
    directAuc: false,
  };
}
